import { useEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { EbError, login } from '../api';
import type { EbSession } from '../types';
import { SecretaryGeneral } from './SecretaryGeneral';
import { watchReducedMotion } from '../browserCompat';

const vertex = `
attribute vec4 aCloud;
attribute vec4 aMotion;
uniform float uTime;
uniform float uProgress;
uniform vec2 uViewport;
varying float vAlpha;
varying float vDepth;
void main(){
  float nearFactor=clamp((9.0-aCloud.z)/15.0,0.1,1.0);
  float rush=1.0+uProgress*(.9+nearFactor*3.4);
  float drift=sin(uTime*aMotion.z+aCloud.x*.45)*.19*(1.0-uProgress*.5);
  vec3 p=vec3(aCloud.x*rush,aCloud.y*rush+drift+aMotion.y*uProgress,aCloud.z-uProgress*(4.0+nearFactor*5.0));
  float aspect=uViewport.x/max(uViewport.y,1.0);
  gl_Position=vec4(p.x/aspect/7.4,p.y/4.5,0.0,1.0);
  gl_PointSize=min(540.0,aCloud.w*(.72+nearFactor*1.55)*(1.0+uProgress*nearFactor*1.8)*(uViewport.y/900.0));
  vAlpha=(.42+nearFactor*.46)*(1.0-uProgress*.62);
  vDepth=nearFactor+aMotion.w*.15;
}`;
const fragment = `
precision mediump float;
varying float vAlpha;
varying float vDepth;
float hash(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
void main(){
  vec2 p=gl_PointCoord*2.0-1.0;
  float r=length(p*vec2(1.0,.8));
  float grain=hash(floor(gl_PointCoord*16.0));
  float body=smoothstep(1.0,.14,r+grain*.12);
  float rim=smoothstep(.94,.48,r)-smoothstep(.62,.16,r);
  vec3 shadow=vec3(.025,.040,.020);
  vec3 olive=vec3(.15,.18,.085);
  vec3 gold=vec3(.72,.57,.20);
  vec3 color=mix(shadow,olive,.34+vDepth*.58);
  color=mix(color,gold,rim*(.12+vDepth*.17));
  gl_FragColor=vec4(color,body*vAlpha);
}`;

function createShader(gl: WebGLRenderingContext, type: number, source: string) {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('WebGL unavailable');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(shader) ?? 'Shader compilation failed';
    gl.deleteShader(shader);
    throw new Error(message);
  }
  return shader;
}

function CloudCanvas({ progress, reduced }: { progress: number; reduced: boolean }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const progressRef = useRef(progress);
  progressRef.current = progress;
  useEffect(() => {
    const node = canvas.current;
    if (!node || reduced) return;
    const gl = node.getContext('webgl', { alpha: false, antialias: false, powerPreference: 'high-performance' });
    if (!gl) return;
    const vert = createShader(gl, gl.VERTEX_SHADER, vertex);
    const frag = createShader(gl, gl.FRAGMENT_SHADER, fragment);
    const program = gl.createProgram();
    if (!program) return;
    gl.attachShader(program, vert); gl.attachShader(program, frag); gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;
    gl.useProgram(program);
    const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4;
    const cores = navigator.hardwareConcurrency ?? 4;
    const count = memory >= 8 && cores >= 8 ? 132 : memory >= 4 && cores >= 4 ? 88 : 54;
    const stride = 8;
    const data = new Float32Array(count * stride);
    let seed = 2026;
    const random = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
    for (let index = 0; index < count; index++) {
      const near = random(); const side = index % 2 ? -1 : 1; const offset = index * stride;
      data[offset] = side * (.35 + random() * 5.5);
      data[offset + 1] = (random() - .5) * 7.4;
      data[offset + 2] = -6 + near * 14;
      data[offset + 3] = 125 + near * 285 + random() * 110;
      data[offset + 4] = side;
      data[offset + 5] = (random() - .5) * 1.9;
      data[offset + 6] = .28 + random() * 1.1;
      data[offset + 7] = random();
    }
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer); gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
    const cloud = gl.getAttribLocation(program, 'aCloud'); const motion = gl.getAttribLocation(program, 'aMotion');
    gl.enableVertexAttribArray(cloud); gl.vertexAttribPointer(cloud, 4, gl.FLOAT, false, stride * 4, 0);
    gl.enableVertexAttribArray(motion); gl.vertexAttribPointer(motion, 4, gl.FLOAT, false, stride * 4, 4 * 4);
    const time = gl.getUniformLocation(program, 'uTime'); const amount = gl.getUniformLocation(program, 'uProgress'); const viewport = gl.getUniformLocation(program, 'uViewport');
    let frame = 0; let active = true;
    const draw = (now: number) => {
      if (!active) return;
      const scale = Math.min(devicePixelRatio || 1, memory <= 2 ? 1 : 1.5);
      const width = Math.max(1, Math.floor(node.clientWidth * scale)); const height = Math.max(1, Math.floor(node.clientHeight * scale));
      if (node.width !== width || node.height !== height) { node.width = width; node.height = height; }
      gl.viewport(0, 0, width, height); gl.clearColor(.006, .01, .005, 1); gl.clear(gl.COLOR_BUFFER_BIT);
      gl.enable(gl.BLEND); gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      gl.uniform1f(time, now / 1000); gl.uniform1f(amount, progressRef.current); gl.uniform2f(viewport, width, height);
      gl.drawArrays(gl.POINTS, 0, count); frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    return () => {
      active = false; cancelAnimationFrame(frame); gl.deleteBuffer(buffer); gl.deleteProgram(program);
      gl.deleteShader(vert); gl.deleteShader(frag); gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
  }, [reduced]);
  return <canvas ref={canvas} className="cloud-canvas" aria-hidden="true" />;
}

function CloudSculpture() {
  return <svg className="cloud-sculpture" viewBox="0 0 1600 900" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
    <defs>
      <linearGradient id="cloudFar" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#0b100b" /><stop offset=".48" stopColor="#283222" /><stop offset=".82" stopColor="#566044" /><stop offset="1" stopColor="#928a5b" />
      </linearGradient>
      <linearGradient id="cloudMid" x1=".15" y1="0" x2=".8" y2="1">
        <stop offset="0" stopColor="#080c08" /><stop offset=".42" stopColor="#26311f" /><stop offset=".76" stopColor="#626b4b" /><stop offset="1" stopColor="#aaa16b" />
      </linearGradient>
      <linearGradient id="cloudNear" x1=".1" y1="0" x2=".8" y2="1">
        <stop offset="0" stopColor="#050805" /><stop offset=".38" stopColor="#202b1b" /><stop offset=".72" stopColor="#69724e" /><stop offset="1" stopColor="#c0b47a" />
      </linearGradient>
      <radialGradient id="cloudGold" cx="56%" cy="42%" r="66%">
        <stop offset="0" stopColor="#d5be69" stopOpacity=".42" /><stop offset=".34" stopColor="#81783d" stopOpacity=".18" /><stop offset="1" stopColor="#0a0d08" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="cloudTop" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#070a07" /><stop offset=".62" stopColor="#303b27" /><stop offset="1" stopColor="#8e875b" />
      </linearGradient>
      <linearGradient id="cloudBottom" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#8e875b" /><stop offset=".38" stopColor="#303b27" /><stop offset="1" stopColor="#070a07" />
      </linearGradient>
      <path id="leftFar" d="M0 0H530c44 25 64 72 45 112 65-6 114 29 119 78 4 39-22 70-60 83 71 16 103 60 84 114-11 31-39 52-76 61 74 28 98 76 65 132-19 33-55 48-97 50 52 41 58 91 13 134-33 32-81 39-134 22 16 52-8 92-72 114H0Z" />
      <path id="leftMid" d="M0 0H370c55 14 85 52 76 98 67-18 123 6 139 57 13 42-8 79-55 101 77 3 123 39 121 91-2 43-35 75-88 90 69 25 93 66 69 119-18 39-58 61-106 59 44 47 39 94-16 130-39 25-86 25-135 0 2 65-41 111-129 155H0Z" />
      <path id="leftNear" d="M0 0H230c56 32 69 77 37 122 72-20 124 9 136 64 9 44-18 81-70 99 69 17 101 59 82 111-14 39-50 63-96 65 51 44 52 91 3 132-35 29-79 34-128 14 20 55-7 100-79 137L0 787Z" />
      <path id="topCloud" d="M0 0H1600V105c-98 61-198 66-286 12-28 80-105 109-191 68-42-20-68-51-76-91-63 67-143 77-214 31-21-14-36-30-45-48-51 72-132 86-206 42-31-19-51-44-61-74-76 76-165 79-239 8C199 104 105 95 0 39Z" />
      <path id="bottomCloud" d="M0 900V790c91-64 186-70 274-18 32-72 105-98 184-63 50 22 79 58 85 102 70-64 151-68 219-13 18 14 31 30 39 47 56-67 137-76 207-27 26 18 44 42 53 70 74-73 160-77 238-12 92-47 192-46 301 3V900Z" />
    </defs>
    <rect width="1600" height="900" fill="url(#cloudGold)" opacity=".42" />
    <g className="cloud-piece cloud-top cloud-far"><use href="#topCloud" fill="url(#cloudTop)" /></g>
    <g className="cloud-piece cloud-bottom cloud-far"><use href="#bottomCloud" fill="url(#cloudBottom)" /></g>
    <g className="cloud-piece cloud-left cloud-far"><use href="#leftFar" fill="url(#cloudFar)" /></g>
    <g className="cloud-piece cloud-right cloud-far"><use href="#leftFar" fill="url(#cloudFar)" transform="translate(1600 0) scale(-1 1)" /></g>
    <g className="cloud-piece cloud-left cloud-mid"><use href="#leftMid" fill="url(#cloudMid)" /></g>
    <g className="cloud-piece cloud-right cloud-mid"><use href="#leftMid" fill="url(#cloudMid)" transform="translate(1600 0) scale(-1 1)" /></g>
    <g className="cloud-piece cloud-left cloud-near"><use href="#leftNear" fill="url(#cloudNear)" /></g>
    <g className="cloud-piece cloud-right cloud-near"><use href="#leftNear" fill="url(#cloudNear)" transform="translate(1600 0) scale(-1 1)" /></g>
  </svg>;
}

function TunnelArchitecture() {
  const frames = Array.from({ length: 11 }, (_, index) => ({
    depth: 240 + index * 180,
    opacity: .94 - index * .045,
  }));
  return <div className="tunnel-world" aria-hidden="true">
    <div className="tunnel-portal" />
    <div className="tunnel-rails">{Array.from({ length: 8 }, (_, index) => <i key={index} style={{ '--rail': index } as CSSProperties} />)}</div>
    <div className="tunnel-frames">
      {frames.map((frame, index) => <i key={frame.depth} className="tunnel-frame" style={{ '--depth': `${frame.depth}px`, '--frame-opacity': frame.opacity, '--frame-rotation': `${(index - 5) * .08}deg` } as CSSProperties} />)}
    </div>
    <div className="tunnel-inscriptions left"><span>CONSENSUS</span><span>MANDATE</span><span>RESOLUTION</span></div>
    <div className="tunnel-inscriptions right"><span>DIPLOMACY</span><span>DELIBERATION</span><span>IMPACT</span></div>
    <div className="tunnel-title"><span>DISMUN 2026</span><strong>THE DIPLOMATIC<br />PASSAGE</strong><small>Scroll through the cloud corridor</small></div>
  </div>;
}

export function CloudGate({ onEnter }: { onEnter: (session: EbSession) => void }) {
  const root = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);
  const [reduced, setReduced] = useState(false);
  const [skipAnimation, setSkipAnimation] = useState(false);
  const [name, setName] = useState(''); const [code, setCode] = useState(''); const [accountLinkCode, setAccountLinkCode] = useState('');
  const [show, setShow] = useState(false); const [focus, setFocus] = useState<'name'|'code'|'account-link'|null>(null);
  const [busy, setBusy] = useState(false); const [error, setError] = useState(''); const [success, setSuccess] = useState(false);
  const [pointer, setPointer] = useState({ x: 0, y: 0 });
  useEffect(() => {
    return watchReducedMotion((matches) => { setReduced(matches); if (matches) setProgress(1); });
  }, []);
  useEffect(() => {
    if (reduced) return;
    let frame = 0;
    const update = () => {
      frame = 0; const node = root.current; if (!node) return;
      const max = Math.max(1, node.offsetHeight - innerHeight);
      setProgress(Math.max(0, Math.min(1, -node.getBoundingClientRect().top / max)));
    };
    const scroll = () => { if (!frame) frame = requestAnimationFrame(update); };
    update(); addEventListener('scroll', scroll, { passive: true }); addEventListener('resize', scroll);
    return () => { removeEventListener('scroll', scroll); removeEventListener('resize', scroll); if (frame) cancelAnimationFrame(frame); };
  }, [reduced]);
  const visibleProgress = skipAnimation ? 1 : progress;
  useEffect(() => {
    if (visibleProgress < .96) return;
    const previous = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [visibleProgress]);
  async function submit(event: FormEvent) {
    event.preventDefault(); setBusy(true); setError('');
    try {
      const session = await login(name.trim(), code, accountLinkCode); setSuccess(true);
      setTimeout(() => onEnter(session), reduced ? 120 : 650);
    } catch (problem) { setError(problem instanceof EbError ? problem.message : 'Unable to connect securely.'); }
    finally { setBusy(false); }
  }
  const style = { '--cloud-progress': visibleProgress, '--eye-x': `${pointer.x * 4}px`, '--eye-y': `${pointer.y * 3}px` } as CSSProperties;
  return <div ref={root} className={`cloud-entry ${reduced ? 'reduced' : ''} ${success ? 'success' : ''}`} style={style}
    onPointerMove={(event) => { const box = event.currentTarget.getBoundingClientRect(); setPointer({ x: Math.max(-1, Math.min(1, (event.clientX - box.left) / box.width * 2 - 1)), y: Math.max(-1, Math.min(1, (event.clientY - box.top) / box.height * 2 - 1)) }); }}>
    <section className="cloud-stage">
      <CloudCanvas progress={visibleProgress} reduced={reduced} />
      <CloudSculpture />
      <TunnelArchitecture />
      <div className="cloud-rift" aria-hidden="true" />
      <div className="atmosphere" aria-hidden="true" />
      <div className="crest-build" aria-hidden="true"><i /><i /><img src="/brand/crest-384.png" alt="" /><span>Executive Board · DISMUN 2026</span></div>
      <div className="scroll-cue" aria-hidden="true"><span>Scroll to enter</span><i /></div>
      {!reduced && visibleProgress <= .78 && <button type="button" className="skip-cloud-entry" onClick={() => setSkipAnimation(true)}>Open EB login</button>}
      <div className={`login-reveal ${visibleProgress > .78 ? 'ready' : ''}`} aria-hidden={visibleProgress <= .78}>
        <div className="login-glass">
          <aside className="guardian-panel">
            <img src="/brand/crest-384.png" alt="DISMUN crest" />
            <SecretaryGeneral className={`${focus === 'code' ? 'covering' : ''} ${show ? 'peeking' : ''} ${error ? 'confused' : ''} ${success ? 'approved' : ''}`} />
            <strong>Your Secretary General</strong>
            <p>{success ? 'Identity verified. Opening your committee.' : error ? 'That access code was not accepted.' : focus === 'code' ? 'Your access code remains private.' : 'Watching over the Executive Board gateway.'}</p>
          </aside>
          <form className="login-form" onSubmit={submit}>
            <p className="eyebrow">DISMUN 2026 · SECURE OPERATIONS</p>
            <h1>Executive Board Access</h1>
            <p>Enter your display name, the normal EB access code, and the EB assigned code generated for your existing profile.</p>
            <label><span>Display name</span><input autoComplete="username" value={name} maxLength={100} onFocus={() => setFocus('name')} onBlur={() => setFocus(null)} onChange={(event) => setName(event.target.value)} /></label>
            <label><span>EB access code</span><div className="secret-input"><input type={show ? 'text' : 'password'} autoComplete="current-password" value={code} maxLength={128} onFocus={() => setFocus('code')} onBlur={() => setFocus(null)} onChange={(event) => setCode(event.target.value)} /><button type="button" aria-label={show ? 'Hide access code' : 'Show access code'} onClick={() => setShow((value) => !value)}>{show ? <EyeOff /> : <Eye />}</button></div></label>
            <label><span>EB assigned code</span><input className="account-link-input" autoComplete="one-time-code" value={accountLinkCode} maxLength={11} placeholder="XXXXX-XXXXX" onFocus={() => setFocus('account-link')} onBlur={() => setFocus(null)} onChange={(event) => setAccountLinkCode(event.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))} /></label>
            <div className="security-note"><ShieldCheck /> The assigned code selects your existing EB profile. The display name cannot create or switch the linked account.</div>
            {error && <output className="form-error">{error}</output>}
            <button className="gold-button" disabled={busy || name.trim().length < 2 || code.length < 12 || accountLinkCode.replace(/[^A-Z0-9]/g, '').length !== 10}>{busy ? 'Verifying secure identity…' : 'Continue to EB panel'}</button>
          </form>
        </div>
      </div>
    </section>
  </div>;
}
